import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink, Flame, Infinity as InfinityIcon, MapPin } from 'lucide-react';
import GpxMap from './GpxMap';

/**
 * Kopf der Event-Seite auf dem Handy (26.10.2026, Vorbild Strava und Garmin aus Mobbin).
 *
 * Oben steht, was das Event an Bild hergibt, in dieser Reihenfolge: **GPX-Strecke** (mit
 * Höhenprofil), sonst **Foto**, sonst eine **Karte vom Startort**, sonst nichts. Darunter immer
 * gleich: Datumskachel, Kategorie und Titel, die Terminzeile, die Eckdaten als 2×2-Raster und der
 * Startort mit „Route“. Was fehlt, fällt weg; kein Platzhalter, keine leere Kachel.
 *
 * Die Seite rechnet die Angaben (Strecken, Höhenmeter, Wetter), der Kopf zeichnet nur.
 */
export type EventKopfBild =
  | { art: 'gpx'; url: string; titel: string; routeUrl?: string }
  | { art: 'foto'; url: string; fokus: string; nachweis?: string | null; ki?: boolean; onFehler: () => void }
  | { art: 'ort'; lat: number; lng: number }
  | { art: 'leer'; text: string }
  /** Eingebettete Strecke (Komoot, AllTrails) ohne eigene GPX-Datei; nur am Rechner. */
  | { art: 'einbettung'; url: string; titel: string }
  | null;

/** Kurzfassung des Startorts für die Sprechblase auf der Karte. */
export interface EventKopfBlase { titel: string; unter?: string; mapsUrl: string }

/** So weit steht der Pin unter der Kartenmitte, wenn eine Blase darüber steht (passt zu `.edk-blase`). */
const BLASE_PIN_TIEFER = 52;

/** Eine Strecke in der Wahl unter dem Bild. */
export interface EventKopfStrecke { key: string | number; gross: string; einheit: string; unter?: string | null }

export interface EventKopfKachel { icon: React.ReactNode; label: string; wert: string; zusatz?: string; leise?: boolean; ton?: 'positiv' }

interface Props {
  bild: EventKopfBild;
  /** Datumskachel; `null` bei einem permanenten Event. */
  datum: { monat: string; tag: string; wochentag: string } | null;
  farbe: string;
  kicker: string;
  titel: string;
  abgesagt?: boolean;
  /** Zeile unter dem Titel, nur wo die Kachel nicht alles sagt (mehrtägig, permanent). */
  wann?: string;
  /** Abgesagt, ausgebucht, vorläufig: als Hinweis im Kopf, nicht als Band über dem Bild. */
  status?: { text: string; ton: 'rot' | 'gelb' };
  countdown?: string;
  /** „Beliebt“ in der Kicker-Zeile, wie am Rechner; allein unter dem Titel hing es in der Luft. */
  beliebt?: string;
  kacheln: EventKopfKachel[];
  ort?: { zeile1: string; zeile2?: string; mapsUrl?: string };
  /** Sprechblase auf der Startort-Karte; dann entfällt die Ortszeile darunter. */
  blase?: EventKopfBlase | null;
  /**
   * Streckenwahl direkt unter dem Bild (ab zwei Strecken), wie bei Strava und komoot: ein Tipp
   * wechselt die Streckenkarte darüber, die Wirkung steht im selben Blick.
   */
  strecken?: EventKopfStrecke[];
  gewaehlt?: number;
  onWahl?: (i: number) => void;
  /** Anmeldestand, Glocke: was unter der Terminzeile steht. */
  children?: React.ReactNode;
}

/**
 * Kleine Karte vom Startort, ohne Bedienung: nur „hier geht es los“. `tiefer` schiebt den Pin um
 * so viele Pixel unter die Mitte, damit über ihm Platz für eine Sprechblase bleibt.
 */
export const StartortKarte: React.FC<{ lat: number; lng: number; tiefer?: number }> = ({ lat, lng, tiefer = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const map = L.map(ref.current, { zoomControl: false, attributionControl: true, dragging: false, scrollWheelZoom: false, doubleClickZoom: false, touchZoom: false, boxZoom: false, keyboard: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>', className: 'karte-kacheln' }).addTo(map);
    // Nachweis unten rechts und ohne Leaflet-Präfix (Flagge, „Leaflet“): Pflicht ist nur „© OpenStreetMap“.
    // Links unten liegt die Datumskachel über der Karte.
    map.attributionControl.setPrefix(false);
    map.attributionControl.setPosition('bottomright');
    // Mittig auf den Startort (bzw. `tiefer` darunter); der Pin steht mit der Spitze auf dem Punkt.
    L.marker([lat, lng], {
      interactive: false,
      icon: L.divIcon({
        className: '', iconSize: [30, 40], iconAnchor: [15, 40],
        html: '<svg width="30" height="40" viewBox="-1 -1 26 34" style="display:block;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35))">'
          + '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="#922C57" stroke="#fff" stroke-width="1.8"/>'
          + '<circle cx="12" cy="12" r="4.6" fill="#fff"/></svg>',
      }),
    }).addTo(map);
    // Beim Aufbau ist der Kasten oft noch ohne Höhe; Leaflet rechnet die Mitte dann gegen 0 px, und der
    // Pin stünde am oberen Rand. Nach dem ersten Zeichnen nachmessen und neu zentrieren.
    const zentrieren = () => {
      map.setView([lat, lng], 14, { animate: false });
      if (tiefer) map.panBy([0, -tiefer], { animate: false });
    };
    zentrieren();
    const t = window.setTimeout(() => { map.invalidateSize(); zentrieren(); }, 60);
    return () => { window.clearTimeout(t); map.remove(); };
  }, [lat, lng, tiefer]);
  return <div ref={ref} className="edk-ortkarte" aria-label="Karte vom Startort" />;
};

/**
 * Was das Event an Bild hergibt (GPX-Strecke, Foto, Startort-Karte, Hinweis), als Fläche. Steht im
 * Kopf auf dem Handy und am Rechner im Strecken-Block (`EventDetailDesktop`); dort größer, mit
 * vollem Höhenprofil.
 */
export const EventKopfBildFlaeche: React.FC<{
  bild: EventKopfBild;
  abgesagt?: boolean;
  /** Sprechblase über dem Pin der Startort-Karte: kurzer Name, Ort; ein Tipp öffnet die Route. */
  blase?: EventKopfBlase | null;
  /** Am Rechner: höhere Karte, Profil in voller Höhe, keine Kopfzeile am Profil. */
  gross?: boolean;
}> = ({ bild, abgesagt, blase, gross }) => (
  <>
    {bild?.art === 'gpx' && (
      <div className="edk-bild edk-bild--gpx"><GpxMap gpxUrl={bild.url} title={bild.titel} routeUrl={bild.routeUrl} height={gross ? 380 : 210} compactProfile={!gross} profileHeader={!gross} /></div>
    )}
    {bild?.art === 'foto' && (
      <figure className="edk-bild edk-bild--foto">
        <img src={bild.url} alt="" onError={bild.onFehler} style={{ objectPosition: bild.fokus }} className={abgesagt ? 'ed-cancelled-img' : undefined} />
        {(bild.nachweis || bild.ki) && (
          <figcaption>{bild.nachweis}{bild.ki && <span className="edk-ki">KI-generiert</span>}</figcaption>
        )}
      </figure>
    )}
    {/* Startort-Karte mit Sprechblase über dem Pin (kurz: Name mit Link-Symbol, Ort). Ein Schild quer über
        die Karte verdeckte die Gegend um den Pin. */}
    {bild?.art === 'ort' && (
      <div className="edk-bild edk-bild--ort">
        <StartortKarte lat={bild.lat} lng={bild.lng} tiefer={blase ? BLASE_PIN_TIEFER : 0} />
        {blase && (
          <a className="edk-blase" href={blase.mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={`Route zu ${blase.titel}`}>
            <strong><span className="edk-blase-titel">{blase.titel}</span><ExternalLink size={13} aria-hidden /></strong>
            {blase.unter && <span>{blase.unter}</span>}
          </a>
        )}
      </div>
    )}
    {bild?.art === 'leer' && <div className="edk-bild edk-bild--leer"><span>{bild.text}</span></div>}
    {bild?.art === 'einbettung' && (
      <div className="edk-bild edk-bild--einbettung">
        <iframe src={bild.url} title={bild.titel} width="100%" height={480} frameBorder="0" scrolling="no" style={{ display: 'block' }} />
      </div>
    )}
  </>
);

/** Wochentag für die Kachel: „Samstag“ wird „Sa“, eine Spanne („Sa–So“) bleibt ganz. */
export const kurzerWochentag = (wochentag: string | null | undefined): string =>
  !wochentag ? '' : wochentag.includes('–') ? wochentag : wochentag.slice(0, 2);

/** Datumskachel: Monat in der Farbe der Disziplin, Tag groß, Wochentag; permanent mit Unendlich-Zeichen. */
export const DatumsKachel: React.FC<{
  datum: { monat: string; tag: string; wochentag: string } | null;
  farbe: string;
  abgesagt?: boolean;
  className?: string;
}> = ({ datum, farbe, abgesagt, className }) => (
  <div className={`edk-kachel${className ? ` ${className}` : ''}`} style={{ ['--edk-farbe' as string]: abgesagt ? '#B9B3B3' : farbe }}>
    {datum ? (
      <>
        <span className="edk-monat">{datum.monat}</span>
        <span className={datum.tag.length > 2 ? 'edk-tag edk-tag--spanne' : 'edk-tag'}>{datum.tag}</span>
        <span className="edk-wochentag">{datum.wochentag}</span>
      </>
    ) : (
      <>
        <span className="edk-monat">Immer</span>
        <span className="edk-tag"><InfinityIcon size={20} aria-hidden /></span>
        <span className="edk-wochentag" />
      </>
    )}
  </div>
);

const EventDetailKopf: React.FC<Props> = ({ bild, datum, farbe, kicker, titel, abgesagt, wann, countdown, beliebt, kacheln, ort, blase, strecken, gewaehlt = 0, onWahl, status, children }) => {
  // Die Wahl steuert nur dann etwas Sichtbares, wenn oben eine Streckenkarte steht. Dann sitzt sie
  // direkt darunter. Sonst (Foto, Startort, kein Bild) ist sie eine Auskunft und steht bei den
  // Eckdaten, ohne gewählten Zustand.
  const steuert = bild?.art === 'gpx' || bild?.art === 'leer';
  const reihe = strecken && strecken.length > 1 ? (
    <div className={`edk-strecken${steuert ? ' edk-strecken--am-bild' : ' edk-strecken--info'}`} role={steuert ? 'group' : undefined} aria-label={steuert ? 'Strecke wählen' : 'Strecken'}>
      {strecken.map((st, i) => steuert ? (
        <button key={st.key} type="button" className={`edk-strecke${i === gewaehlt ? ' edk-strecke--an' : ''}`}
          aria-pressed={i === gewaehlt} onClick={() => onWahl?.(i)}>
          <span className="edk-strecke-gross">{st.gross}<small>{st.einheit}</small></span>
          {st.unter && <span className="edk-strecke-unter">{st.unter}</span>}
        </button>
      ) : (
        <span key={st.key} className="edk-strecke">
          <span className="edk-strecke-gross">{st.gross}<small>{st.einheit}</small></span>
          {st.unter && <span className="edk-strecke-unter">{st.unter}</span>}
        </span>
      ))}
    </div>
  ) : null;
  return (
  <section className={`edk${bild ? ` edk--mit-bild edk--${bild.art}` : ''}`}>
    <EventKopfBildFlaeche bild={bild} abgesagt={abgesagt} blase={blase} />
    {steuert && reihe}


    <div className="edk-titelzeile">
      <DatumsKachel datum={datum} farbe={farbe} abgesagt={abgesagt} />
      <div className="edk-titel-text">
        <div className="edk-kicker" style={{ color: abgesagt ? '#6A6A6A' : farbe }}>
          <span className="edk-raute" style={{ background: abgesagt ? '#B9B3B3' : farbe }} />{kicker}
          {beliebt && <span className="ecb-popular edk-beliebt"><Flame size={11} aria-hidden />{beliebt}</span>}
        </div>
        <h1 className="edk-titel" style={abgesagt ? { textDecoration: 'line-through' } : undefined}>{titel}</h1>
        {/* Wann: in der Titelspalte, bündig mit Kategorie und Titel, nicht als eigene Zeile unter der Kachel. */}
        {(wann || countdown) && (
          <div className="edk-wann">
            {wann && <span>{wann}</span>}
            {wann && countdown && <span aria-hidden>·</span>}
            {countdown && <span className="edk-countdown">{countdown}</span>}
          </div>
        )}
        {children && <div className="edk-zusatz">{children}</div>}
      </div>
    </div>

    {status && <div className={`edk-status edk-status--${status.ton}`} role="status">{status.text}</div>}

    {kacheln.length > 0 && (
      <div className={`edk-raster${kacheln.length === 1 ? ' edk-raster--eins' : ''}`}>
        {kacheln.map(k => (
          <div key={k.label} className="edk-zelle">
            <span className="edk-zelle-label">{k.icon}{k.label}</span>
            <strong className={k.leise ? 'edk-zelle-wert edk-zelle-wert--leise' : k.ton === 'positiv' ? 'edk-zelle-wert edk-zelle-wert--positiv' : 'edk-zelle-wert'}>{k.wert}</strong>
            {k.zusatz && <span className="edk-zelle-zusatz">{k.zusatz}</span>}
          </div>
        ))}
      </div>
    )}

    {!steuert && reihe}

    {ort && !(bild?.art === 'ort' && blase) && (
      <div className="edk-ort">
        <span className="edk-ort-icon"><MapPin size={18} aria-hidden /></span>
        <span className="edk-ort-text"><strong>{ort.zeile1}</strong>{ort.zeile2 && <span>{ort.zeile2}</span>}</span>
        {ort.mapsUrl && (
          <a className="edk-ort-route" href={ort.mapsUrl} target="_blank" rel="noopener noreferrer">Route<ExternalLink size={13} aria-hidden /></a>
        )}
      </div>
    )}
  </section>
  );
};

export default EventDetailKopf;
